/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */
import { g17ecoApi, transformResponse } from '@api/g17ecoApi';
import { IntegrationConnectionInfo, IntegrationProvider, IntegrationProviderExtended, ProviderSetupInfo, SetupData } from '@g17eco/types/app-integration';
import { ValueData } from '../types/universalTrackerValue';


interface PartnerGet {
  initiativeId: string,
  code: string
}

interface InitiativeAppIntegrationsResponse {
  allProviders: IntegrationProvider[];
  usedProviders: IntegrationProviderExtended[];
}

export const integrationsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: ['integrations', 'integration', 'integration-setup-info'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listInitiativeAppIntegrations: builder.query<InitiativeAppIntegrationsResponse, string>({
        transformResponse,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/integrations`,
          method: 'get',
        }),
        providesTags: ['integrations']
      }),
      getInitiativeAppIntegration: builder.query<ProviderSetupInfo, PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration-setup-info', id: arg.code }],
      }),
      getUsedInitiativeAppIntegration: builder.query<
        IntegrationConnectionInfo<{ answers: { valueData: ValueData }[] }>,
        PartnerGet
      >({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/used`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration', id: arg.code }],
      }),
      createInitiativeAppIntegration: builder.mutation<ProviderSetupInfo, SetupData>({
        transformResponse,
        query: ({ initiativeId, code, ...data }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}`,
          method: 'post',
          data: data,
        }),
        invalidatesTags: (_result, _error, agr) => ['integrations', { type: 'integration-setup-info', id: agr.code }],
      }),
    }),
  });

export const {
  useGetInitiativeAppIntegrationQuery,
  useListInitiativeAppIntegrationsQuery,
  useGetUsedInitiativeAppIntegrationQuery,
  useCreateInitiativeAppIntegrationMutation,
} = integrationsApi;
